import { useState } from "react";
import { ArrowRight, Layers, Shield, Activity, Zap, Circle, Star } from "lucide-react";
import { SDL_LOGO } from "@/lib/assets";

const services = [
  { icon: <Layers size={22} strokeWidth={1.5} />, title: "Dental Implants", subtitle: "Single · Multiple · Full-Arch", desc: "From single-tooth replacement to full-arch reconstruction. Complex cases, bone loss, and previous implant failures welcome.", featured: true },
  { icon: <Shield size={22} strokeWidth={1.5} />, title: "Bone Grafting", subtitle: "Augmentation & Sinus Lifts", desc: "Advanced bone regeneration including socket preservation, block grafts, and maxillary sinus augmentation." },
  { icon: <Activity size={22} strokeWidth={1.5} />, title: "Periodontics", subtitle: "Gum Health & Surgery", desc: "Specialist management of gum disease, regenerative surgery, soft tissue grafting, and crown lengthening." },
  { icon: <Zap size={22} strokeWidth={1.5} />, title: "Endodontics", subtitle: "Root Canal & Microsurgery", desc: "Complex root canal treatments and apicoectomy using the latest rotary and microscope-assisted techniques." },
  { icon: <Circle size={22} strokeWidth={1.5} />, title: "Prosthodontics", subtitle: "Full-Mouth Rehabilitation", desc: "Comprehensive restorative planning — crowns, bridges, dentures, and full-mouth reconstruction with precision fit." },
  { icon: <Star size={22} strokeWidth={1.5} />, title: "Complex Restoration", subtitle: "Multi-Disciplinary Care", desc: "Difficult, previously failed, or multi-specialist cases coordinated under one consultant's clinical direction." },
];

export default function PracticeSection({ onBook }) {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section id="practice" data-testid="practice-section" className="py-24 md:py-32" style={{ background: '#16110F' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="reveal mb-16">
          {/* SDL Logo — off-white/cream, matches section text */}
          <div className="mb-5 overflow-hidden" style={{ maxWidth: '200px', height: '48px' }}>
            <img
              src={SDL_LOGO}
              alt="Specialist Dental London"
              className="h-full w-auto object-contain"
              style={{ filter: 'brightness(0) invert(1)', opacity: 0.88 }}
            />
          </div>

          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px" style={{ background: '#D4AF37' }} />
            <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#D4AF37' }}>Specialist Dental London</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <h2 className="font-cormorant font-light leading-tight" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', color: '#FDF8ED' }}>
              Consultant-Led Dental Care.<br />
              <em style={{ color: '#D4AF37' }}>No compromise.</em>
            </h2>
            <p className="font-outfit text-sm leading-relaxed max-w-xs" style={{ color: '#A89886' }}>
              Every patient is assessed and treated by Dr. Romeed — a GDC-registered specialist in four dental disciplines with over two decades of complex restorative experience.
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {services.map((svc, i) => (
            <div
              key={svc.title}
              data-testid={`service-card-${i}`}
              className="reveal transition-all duration-300"
              style={{
                background: hoveredCard === i ? '#211915' : '#1A140F',
                border: `1px solid ${hoveredCard === i ? '#D4AF37' : '#3A2E28'}`,
                padding: '28px',
                boxShadow: hoveredCard === i ? '0 0 28px -8px rgba(212,175,55,0.25)' : 'none',
                animationDelay: `${i * 0.08}s`,
              }}
              onMouseEnter={() => setHoveredCard(i)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {svc.featured && (
                <div className="inline-block font-outfit text-xs tracking-widest uppercase px-2 py-0.5 mb-4" style={{ background: '#D4AF37', color: '#16110F' }}>
                  Primary Speciality
                </div>
              )}
              <div className="mb-4 transition-colors duration-200" style={{ color: hoveredCard === i ? '#D4AF37' : '#A89886' }}>
                {svc.icon}
              </div>
              <h3 className="font-cormorant text-xl font-medium mb-1" style={{ color: '#FDF8ED' }}>{svc.title}</h3>
              <p className="font-outfit text-xs tracking-wider uppercase mb-3" style={{ color: '#D4AF37', opacity: 0.65 }}>{svc.subtitle}</p>
              <p className="font-outfit text-sm leading-relaxed" style={{ color: '#A89886' }}>{svc.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="reveal grid grid-cols-2 md:grid-cols-4 gap-px mb-16" style={{ border: '1px solid #3A2E28', background: '#3A2E28' }}>
          {[
            { num: '25+', label: 'Years in Practice' },
            { num: '4', label: 'Royal College Fellowships' },
            { num: 'PhD', label: 'University of Manchester' },
            { num: 'GDC', label: 'Specialist Register' },
          ].map(stat => (
            <div key={stat.label} className="flex flex-col items-center justify-center py-8 gap-2 text-center" style={{ background: '#16110F' }}>
              <span className="font-cormorant text-3xl font-light" style={{ color: '#D4AF37' }}>{stat.num}</span>
              <span className="font-outfit text-xs tracking-widest uppercase" style={{ color: '#A89886' }}>{stat.label}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="reveal flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <p className="font-cormorant text-2xl font-light italic" style={{ color: '#FDF8ED' }}>
            Ready to restore your smile with a consultant?
          </p>
          <button data-testid="practice-book-btn" onClick={() => onBook('patient')} className="flex items-center gap-3 font-outfit text-sm font-medium px-8 py-4 transition-all duration-200 hover:opacity-90 active:scale-95 whitespace-nowrap" style={{ background: '#D4AF37', color: '#16110F' }}>
            Book a Patient Consultation <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
